import { useEffect, useState, type ReactNode } from 'react';
import { useCan } from '../../lib/auth/session.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Section, Switch } from '../../components/ui/Form.js';
import { ChipInput } from '../../components/ui/ChipInput.js';
import { CountryPicker } from './CountryPicker.js';
import {
  useCountries, usePolicies, useAssignments, useSavePolicy, useDeletePolicy, useAssignPolicy,
  normalisePrefix, showPrefix, barePrefix, dialCode, type Policy, type Assignments,
} from './dialPolicies.js';

/**
 * Dialling restrictions for the account.
 *
 * A policy says what may be dialled, by three methods: international dialling
 * (which countries), blocked prefixes, and allowed prefixes. It is attached to
 * the account, to a cost centre or to an extension, and the most specific one
 * attached decides -- except blocked prefixes, which apply from every level, so
 * nothing attached to a seat unblocks what the account blocked.
 *
 * This screen writes policies and attaches them to the account and its cost
 * centres. An extension subscribes on its own Dialling restrictions tab, which
 * also shows the effect on that seat.
 */
export function DialPolicyTab({ tenantId }: { tenantId: string }) {
  const can = useCan();
  const writable = can('dialpermission:write');
  const policies = usePolicies(tenantId);
  const assignments = useAssignments(tenantId);
  const [editing, setEditing] = useState<Policy | null>(null);
  const [creating, setCreating] = useState(false);
  const list = policies.data?.data ?? [];
  const close = () => { setCreating(false); setEditing(null); };

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Dialling restrictions</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            What may be dialled: countries abroad, blocked prefixes and allowed prefixes.
          </p>
        </div>
        <div className="arc-spacer" />
        {writable && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            onClick={() => { setCreating((v) => !v); setEditing(null); }}>
            <Icon name={creating ? 'close' : 'plus'} size={13} />
            {creating ? 'Cancel' : 'New policy'}
          </button>
        )}
      </div>

      {policies.error && <ErrorBanner error={policies.error} />}
      {assignments.error && <ErrorBanner error={assignments.error} />}

      {(creating || editing) && assignments.data && (
        <PolicyEditor key={editing?.id ?? 'new'} tenantId={tenantId} existing={editing}
          homeCountry={assignments.data.home_country} homePrefix={assignments.data.home_prefix}
          onDone={close} />
      )}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Policy</th><th>International</th>
                <th className="arc-td-num">Blocked</th><th className="arc-td-num">Allowed</th>
                <th>Applies to</th><th />
              </tr>
            </thead>
            {policies.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className="arc-row-editable" onDoubleClick={() => { setEditing(p); setCreating(false); }}>
                    <td style={{ fontWeight: 550 }}>{p.name}</td>
                    <td>
                      {p.allow_international
                        ? <Badge tone="info">{countriesLabel(p.allowed_countries.length)}</Badge>
                        : <Badge tone="muted">national only</Badge>}
                    </td>
                    <td className="arc-td-num arc-muted">
                      {p.blocked_prefixes.length || <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-td-num arc-muted">
                      {p.allowed_prefixes.length || <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-muted">{appliesTo(p)}</td>
                    <td className="arc-td-right">
                      {writable && (
                        <button type="button" className="arc-btn arc-btn-sm arc-pill-edit"
                          onClick={() => { setEditing(p); setCreating(false); }}>Edit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {policies.data !== undefined && list.length === 0 && (
          <Empty icon="plan" title="No dialling policies"
            hint="Without one, every extension may dial national numbers and nothing abroad." />
        )}
      </div>

      {assignments.data && (
        <AppliesTo tenantId={tenantId} data={assignments.data} policies={list} writable={writable} />
      )}
    </>
  );
}

const countriesLabel = (n: number) => `${n} ${n === 1 ? 'country' : 'countries'}`;

function appliesTo(p: Policy): ReactNode {
  const parts = [
    p.tenant_count > 0 ? 'account default' : null,
    p.group_count > 0 ? `${p.group_count} cost ${p.group_count === 1 ? 'centre' : 'centres'}` : null,
    p.extension_count > 0 ? `${p.extension_count} ${p.extension_count === 1 ? 'extension' : 'extensions'}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : <span className="arc-subtle">nothing yet</span>;
}

/**
 * Attaching policies above the seat: the account default and each cost centre.
 *
 * Saved one row at a time, with a button rather than on change, because each
 * of these widens or narrows what a whole group of handsets can dial.
 */
function AppliesTo({ tenantId, data, policies, writable }: {
  tenantId: string; data: Assignments; policies: Policy[]; writable: boolean;
}) {
  const accountPolicy = policies.find((p) => p.id === data.account_policy_id);
  const own = data.seats_with_own_policy;
  return (
    <Section title="Where policies apply" columns={1}
      description="The most specific policy decides: an extension's own, then its department's, then the account default. Blocked prefixes from all three apply together.">
      <AssignRow tenantId={tenantId} groupId={null} label="Account default"
        detail="Every extension without a policy of its own or its department's"
        current={data.account_policy_id} policies={policies} writable={writable}
        inheritLabel="None — national calls only" />
      {data.groups.map((g) => (
        <AssignRow key={g.id} tenantId={tenantId} groupId={g.id} label={g.name}
          detail={`Department · ${g.extension_count} ${g.extension_count === 1 ? 'extension' : 'extensions'}`}
          current={g.dial_policy_id} policies={policies} writable={writable}
          inheritLabel={`Account default${accountPolicy ? ` (${accountPolicy.name})` : ''}`} />
      ))}
      {own > 0 && (
        <p className="arc-muted" style={{ fontSize: 12.5, margin: 0 }}>
          {own} {own === 1 ? 'extension has' : 'extensions have'} a policy of its own, chosen on
          the extension&apos;s Dialling restrictions tab.
        </p>
      )}
    </Section>
  );
}

function AssignRow({ tenantId, groupId, label, detail, current, policies, writable, inheritLabel }: {
  tenantId: string; groupId: string | null; label: string; detail: string;
  current: string | null; policies: Policy[]; writable: boolean; inheritLabel: string;
}) {
  const assign = useAssignPolicy(tenantId);
  const [value, setValue] = useState(current ?? '');
  useEffect(() => { setValue(current ?? ''); }, [current]);
  const dirty = value !== (current ?? '');
  const id = `dp-assign-${groupId ?? 'account'}`;

  return (
    <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
      <label htmlFor={id} style={{ flex: '1 1 220px', minWidth: 0 }}>
        <span style={{ fontWeight: 550 }}>{label}</span>
        <span className="arc-muted" style={{ display: 'block', fontSize: 12 }}>{detail}</span>
      </label>
      <select id={id} className="arc-select" style={{ flex: '1 1 240px', maxWidth: 340 }}
        value={value} disabled={!writable || assign.isPending}
        onChange={(e) => setValue(e.target.value)}>
        <option value="">{inheritLabel}</option>
        {policies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <div className="arc-row" style={{ gap: 4, minWidth: 128 }}>
        {dirty && (
          <>
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
              disabled={assign.isPending}
              onClick={() => assign.mutate({ groupId, policyId: value || null })}>
              {assign.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => setValue(current ?? '')}>Undo</button>
          </>
        )}
      </div>
    </div>
  );
}

/** One of the three methods: a heading, what it does, and its control. */
function Method({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className="arc-method">
      <h3 className="arc-method-title">{title}</h3>
      <p className="arc-method-hint">{hint}</p>
      {children}
    </section>
  );
}

function PolicyEditor({ tenantId, existing, homeCountry, homePrefix, onDone }: {
  tenantId: string; existing: Policy | null;
  homeCountry: string; homePrefix: string | null;
  onDone: () => void;
}) {
  const save = useSavePolicy(tenantId);
  const remove = useDeletePolicy(tenantId);
  const countries = useCountries();

  const [name, setName] = useState(existing?.name ?? '');
  const [intl, setIntl] = useState(existing?.allow_international ?? false);
  const [premium, setPremium] = useState(existing?.allow_premium ?? false);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(existing?.allowed_countries ?? []));
  const [blocked, setBlocked] = useState<string[]>(() => (existing?.blocked_prefixes ?? []).map(barePrefix));
  const [allowed, setAllowed] = useState<string[]>(() => (existing?.allowed_prefixes ?? []).map(barePrefix));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const list = countries.data?.data ?? [];
  const nameOf = (iso: string) => list.find((c) => c.iso2 === iso)?.name ?? iso;
  const risky = [...new Set(list.filter((c) => c.is_high_risk && picked.has(c.iso2)).map((c) => c.iso2))];
  const validate = (raw: string) => normalisePrefix(raw, homePrefix);
  const show = (v: string) => showPrefix(v, homePrefix);
  // National numbers are always allowed, so an allowed prefix at home does nothing.
  const pointless = homePrefix ? allowed.filter((v) => v.startsWith(homePrefix)) : [];
  const inUse = existing ? existing.tenant_count + existing.group_count + existing.extension_count : 0;

  return (
    <form className="arc-card" style={{ marginBottom: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name: name.trim(),
          allow_international: intl,
          // Kept while international is off, so switching it back on restores them.
          allowed_countries: [...picked],
          allow_premium: premium,
          blocked_prefixes: blocked,
          allowed_prefixes: allowed,
        }, { onSuccess: onDone });
      }}>
      <div className="arc-card-body">
        <div className="arc-field" style={{ margin: 0, maxWidth: 340 }}>
          <label className="arc-label" htmlFor="dp-name">
            {existing ? 'Policy name' : 'New policy'}
          </label>
          <input id="dp-name" className="arc-input" required maxLength={80} value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Sales — international" />
        </div>

        <Method title="International dialling"
          hint={`National numbers${homePrefix ? ` (${dialCode(homePrefix)})` : ''} are always allowed unless a blocked prefix matches. Abroad, only the countries switched on here.`}>
          <div className="arc-row" style={{ gap: 10, marginBottom: 12 }}>
            <Switch id="dp-intl" checked={intl} onChange={setIntl} />
            <label htmlFor="dp-intl" style={{ cursor: 'pointer' }}>Allow calls abroad</label>
          </div>

          {/*
            The continents are always on the page. Behind the switch, nobody found them: the owner
            asked for a continent and country picker that already existed (20 September 2026).
            Switching a country on turns calls abroad on; switching calls abroad off keeps the
            countries, dimmed, for when it goes back on.
          */}
          {countries.isLoading
            ? <div className="arc-skeleton" style={{ height: 220 }} />
            : (
              <>
                {!intl && picked.size > 0 && (
                  <p className="arc-hint" style={{ marginTop: 0 }}>
                    Calls abroad are off, so the countries below are kept but refused.
                  </p>
                )}
                <div style={intl || picked.size === 0 ? undefined : { opacity: 0.6 }}>
                  <CountryPicker countries={list} homeCountry={homeCountry} value={picked}
                    onChange={(next) => {
                      if (next.size > picked.size) setIntl(true);
                      setPicked(next);
                    }} />
                </div>
                {intl && picked.size === 0 && (
                  <p className="arc-hint">No country is switched on yet, so nothing abroad can be dialled.</p>
                )}

                <div className="arc-row" style={{ gap: 10, marginTop: 12, alignItems: 'flex-start' }}>
                  <Switch id="dp-prem" checked={premium} onChange={setPremium} />
                  <label htmlFor="dp-prem" style={{ cursor: 'pointer' }}>
                    Allow high-risk destinations
                    <span className="arc-muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                      Countries marked with a warning stay refused until this is on, even when switched on above.
                    </span>
                  </label>
                </div>

                {risky.length > 0 && (
                  <div className={`arc-banner ${premium ? 'arc-banner-warn' : 'arc-banner-info'}`}
                    style={{ marginTop: 10 }}>
                    <Icon name="warning" size={14} />
                    <span>
                      {risky.map(nameOf).join(', ')}{' '}
                      {premium
                        ? `${risky.length === 1 ? 'is' : 'are'} commonly abused for toll fraud. Keep ${risky.length === 1 ? 'it' : 'them'} only if somebody genuinely calls there.`
                        : `${risky.length === 1 ? 'is' : 'are'} switched on but still refused, because high-risk destinations are not allowed.`}
                    </span>
                  </div>
                )}
              </>
            )}
        </Method>

        <Method title="Blocked prefixes"
          hint="Refused whatever else allows them. Blocks from the account, the department and the extension all apply together. Enter them as dialled: 0861, +882 or 00882.">
          <ChipInput values={blocked} onChange={setBlocked} validate={validate} format={show}
            mono placeholder={homePrefix ? '0861' : '+882'} label="Add a blocked prefix" />
        </Method>

        <Method title="Allowed prefixes"
          hint="Reach numbers abroad that the countries above would refuse, such as one supplier in a country nobody else may call. A blocked prefix still wins.">
          <ChipInput values={allowed} onChange={setAllowed} validate={validate} format={show}
            mono placeholder="+442071234" label="Add an allowed prefix" />
          {pointless.length > 0 && (
            <p className="arc-hint">
              {pointless.map(show).join(', ')} {pointless.length === 1 ? 'is' : 'are'} national,
              and national numbers are allowed already.
            </p>
          )}
        </Method>

        <div className="arc-row" style={{ gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
          <button type="submit" className="arc-btn arc-btn-primary"
            disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Saving…' : existing ? 'Save policy' : 'Create policy'}
          </button>
          <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          <div className="arc-spacer" />
          {existing && (confirmDelete ? (
            <>
              <span className="arc-muted" style={{ fontSize: 12.5 }}>
                {inUse > 0
                  ? `Everything using it (${inUse}) falls back to the level above.`
                  : 'Nothing uses it.'}
              </span>
              <button type="button" className="arc-btn arc-btn-danger arc-btn-sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate(existing.id, { onSuccess: onDone })}>
                {remove.isPending ? 'Deleting…' : 'Delete policy'}
              </button>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          ) : (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" size={13} /> Delete
            </button>
          ))}
        </div>
      </div>
    </form>
  );
}
